/**
 * Spare Parts finder UI: product-number search + filter collection product cards.
 * Depends on window.MicrotecPartsCompat (parts-compat.js).
 *
 * Designed to coexist with collection-body pagination / lazy images:
 * - Does not fight infinite-scroll (parts template uses classic pagination)
 * - Re-inits theme LazyLoadImages after showing cards
 * - Does not hide products when compatibility data is missing
 */
(function () {
  'use strict';

  function whenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function waitForCompat(fn, attempts) {
    attempts = attempts == null ? 40 : attempts;
    if (window.MicrotecPartsCompat) {
      fn(window.MicrotecPartsCompat);
      return;
    }
    if (attempts <= 0) {
      console.error('[PartsFinder] parts-compat.js did not load (MicrotecPartsCompat missing)');
      var status = document.querySelector('[data-parts-status]');
      if (status) {
        status.textContent = 'Parts finder failed to load. Refresh the page.';
        status.setAttribute('data-state', 'error');
      }
      return;
    }
    setTimeout(function () {
      waitForCompat(fn, attempts - 1);
    }, 50);
  }

  function reinitLazyImages() {
    try {
      if (window.themeCore && window.themeCore.LazyLoadImages && typeof window.themeCore.LazyLoadImages.init === 'function') {
        window.themeCore.LazyLoadImages.init();
      }
    } catch (e) {}
  }

  function init(C) {
    var root = document.querySelector('[data-parts-finder]');
    if (!root) return;

    var input = root.querySelector('[data-parts-input]');
    var listbox = root.querySelector('[data-parts-listbox]');
    var submitBtn = root.querySelector('[data-parts-submit]');
    var clearBtn = root.querySelector('[data-parts-clear]');
    var chip = root.querySelector('[data-parts-chip]');
    var chipText = root.querySelector('[data-parts-chip-text]');
    var statusEl = root.querySelector('[data-parts-status]');
    var machinesJson = root.querySelector('[data-parts-machines]');
    var catalogJson = root.querySelector('[data-parts-catalog]');

    if (!input || !submitBtn || !statusEl) {
      console.error('[PartsFinder] Missing required DOM nodes');
      return;
    }

    var includeUniversal = root.getAttribute('data-include-universal') === 'true';
    var unknownText =
      root.getAttribute('data-unknown-text') ||
      'No heat press found for that product number.';
    var chipPrefix = root.getAttribute('data-chip-prefix') || 'Parts for';
    var resultsTemplate = root.getAttribute('data-results-template') || '';
    var emptyMachinesText =
      root.getAttribute('data-empty-machines-text') ||
      'No heat press product numbers loaded. In the theme editor, open Spare parts finder and select heat press collections.';

    var rawMachines = [];
    try {
      rawMachines = JSON.parse((machinesJson && machinesJson.textContent) || '[]') || [];
    } catch (e) {
      console.error('[PartsFinder] Invalid machines JSON', e);
      rawMachines = [];
    }

    var machines = rawMachines
      .map(function (m) {
        return C.buildMachine(m);
      })
      .filter(function (m) {
        return m && (m.sku || m.title);
      });

    var partsCatalog = {};
    try {
      partsCatalog = JSON.parse((catalogJson && catalogJson.textContent) || '{}') || {};
    } catch (e) {
      console.error('[PartsFinder] Invalid catalog JSON', e);
      partsCatalog = {};
    }

    var activeIndex = -1;
    var suggestions = [];
    var selectedMachine = null;
    var filterTimer = null;
    var catalogReady = false;
    var pendingMachine = null;

    function collectionHandle() {
      return (
        root.getAttribute('data-collection-handle') ||
        ((window.location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || '')
      );
    }

    function indexFetchedProduct(p) {
      if (!p || !p.handle) return;
      var variant = (p.variants && p.variants[0]) || {};
      var img =
        (p.images && p.images[0] && p.images[0].src) ||
        (p.featured_image && (p.featured_image.src || p.featured_image)) ||
        '';
      partsCatalog[p.handle] = {
        description: p.body_html || p.description || '',
        title: p.title || '',
        sku: variant.sku || '',
        type: p.product_type || '',
        url: '/products/' + p.handle,
        image: img,
        price: variant.price || '',
        handle: p.handle,
      };
    }

    function loadFullCatalog() {
      var handle = collectionHandle();
      if (!handle) {
        catalogReady = true;
        return Promise.resolve();
      }
      var page = 1;
      function next() {
        return fetch(
          '/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page,
          { credentials: 'same-origin' }
        )
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
          })
          .then(function (data) {
            var products = (data && data.products) || [];
            for (var i = 0; i < products.length; i++) indexFetchedProduct(products[i]);
            if (products.length >= 250) {
              page += 1;
              return next();
            }
          });
      }
      return next()
        .catch(function (e) {
          console.error('[PartsFinder] catalog fetch failed', e);
        })
        .then(function () {
          catalogReady = true;
          if (pendingMachine) {
            var queued = pendingMachine;
            pendingMachine = null;
            applyFilter(queued);
          }
        });
    }

    function setStatus(message, state) {
      statusEl.textContent = message || '';
      if (state) statusEl.setAttribute('data-state', state);
      else statusEl.removeAttribute('data-state');
    }

    function hideList() {
      if (!listbox) return;
      listbox.hidden = true;
      listbox.setAttribute('hidden', 'hidden');
      listbox.setAttribute('aria-hidden', 'true');
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    }

    function showList() {
      if (!listbox) return;
      listbox.hidden = false;
      listbox.removeAttribute('hidden');
      listbox.removeAttribute('aria-hidden');
      input.setAttribute('aria-expanded', 'true');
    }

    function showChip(visible) {
      if (!chip) return;
      if (visible) {
        chip.hidden = false;
        chip.removeAttribute('hidden');
      } else {
        chip.hidden = true;
        chip.setAttribute('hidden', 'hidden');
      }
    }

    function showClearBtn(visible) {
      if (!clearBtn) return;
      if (visible) {
        clearBtn.hidden = false;
        clearBtn.removeAttribute('hidden');
      } else {
        clearBtn.hidden = true;
        clearBtn.setAttribute('hidden', 'hidden');
      }
    }

    function renderSuggestions(items) {
      if (!listbox) return;
      suggestions = items || [];
      listbox.innerHTML = '';
      if (!suggestions.length) {
        hideList();
        return;
      }
      suggestions.forEach(function (m, index) {
        var li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.id = 'PartsFinderOpt-' + index;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'parts-finder__option';
        btn.setAttribute('aria-selected', 'false');
        btn.innerHTML =
          '<span class="parts-finder__option-sku"></span><span class="parts-finder__option-title"></span>';
        btn.querySelector('.parts-finder__option-sku').textContent = m.sku || '—';
        btn.querySelector('.parts-finder__option-title').textContent = m.title || '';
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          selectMachine(m);
        });
        li.appendChild(btn);
        listbox.appendChild(li);
      });
      showList();
    }

    function highlight(index) {
      if (!listbox) return;
      var options = listbox.querySelectorAll('.parts-finder__option');
      for (var i = 0; i < options.length; i++) {
        options[i].setAttribute('aria-selected', i === index ? 'true' : 'false');
      }
      activeIndex = index;
      if (options[index] && options[index].parentElement) {
        input.setAttribute('aria-activedescendant', options[index].parentElement.id);
      }
    }

    function getGrid() {
      return document.querySelector('.js-collection .js-grid-wrapper, .collection .js-grid-wrapper, .js-grid-wrapper');
    }

    function getCards() {
      var grid = getGrid();
      var nodes = grid
        ? grid.querySelectorAll('.collection__item, [data-parts-card]')
        : document.querySelectorAll('[data-parts-card]');
      var list = [];
      for (var i = 0; i < nodes.length; i++) list.push(nodes[i]);
      return list;
    }

    function cardHandle(card) {
      if (!card) return '';
      if (card.getAttribute('data-parts-handle')) {
        return card.getAttribute('data-parts-handle');
      }
      var el = card.querySelector('[data-product-card-handle]');
      if (el) return el.getAttribute('data-product-card-handle') || '';
      var link = card.querySelector('a[href*="/products/"]');
      if (link) {
        var href = link.getAttribute('href') || '';
        var match = href.match(/\/products\/([^/?#]+)/);
        if (match) return decodeURIComponent(match[1]);
      }
      return '';
    }

    function cardTitle(card) {
      if (card.getAttribute('data-parts-title')) {
        return card.getAttribute('data-parts-title');
      }
      var titleEl = card.querySelector('.product-card__title');
      if (titleEl) return (titleEl.textContent || '').trim();
      return '';
    }

    function cardCompat(card) {
      var description = card.getAttribute('data-parts-description') || '';
      var title = cardTitle(card);
      var handle = cardHandle(card);
      var entry = handle && partsCatalog[handle];
      var hasDescription = false;

      if (entry) {
        if (!description && entry.description) description = entry.description;
        if (!title && entry.title) title = entry.title;
      }

      hasDescription = !!(description && String(description).trim());

      // Description only for fitment — do NOT pass tags (tags are often marketing, not fit)
      // Do NOT fold title into body: titles like "Silicon Heating Pad" have no model,
      // and series names in titles would over-match.
      var compat = C.extractCompat(description || '', []);
      compat.insufficientData = !hasDescription;
      return compat;
    }

    function formatPrice(value) {
      if (value == null || value === '') return '';
      var n = Number(value);
      if (isNaN(n)) return String(value);
      return '$' + n.toFixed(2);
    }

    function setCollectionHidden(hidden) {
      document.body.classList.toggle('parts-finder-has-results', !!hidden);
    }

    function resultsEl() {
      return root.querySelector('[data-parts-results]');
    }

    function renderResults(matches) {
      var host = resultsEl();
      if (!host) return;
      host.innerHTML = '';
      if (!matches.length) {
        host.hidden = true;
        host.setAttribute('hidden', 'hidden');
        return;
      }
      matches.forEach(function (entry) {
        var a = document.createElement('a');
        a.className = 'parts-finder-result';
        a.href = entry.url || '/products/' + entry.handle;
        var imgHtml = entry.image
          ? '<img class="parts-finder-result__image" alt="" loading="lazy" decoding="async">'
          : '<div class="parts-finder-result__image" aria-hidden="true"></div>';
        a.innerHTML =
          imgHtml +
          '<span class="parts-finder-result__body">' +
          '<span class="parts-finder-result__title"></span>' +
          (entry.sku ? '<span class="parts-finder-result__sku"></span>' : '') +
          (entry.price ? '<span class="parts-finder-result__price"></span>' : '') +
          '</span>';
        if (entry.image) {
          a.querySelector('img').src = entry.image;
          a.querySelector('img').alt = entry.title || '';
        }
        a.querySelector('.parts-finder-result__title').textContent = entry.title || '';
        var skuNode = a.querySelector('.parts-finder-result__sku');
        if (skuNode) skuNode.textContent = 'Product Number ' + entry.sku;
        var priceNode = a.querySelector('.parts-finder-result__price');
        if (priceNode) priceNode.textContent = formatPrice(entry.price);
        host.appendChild(a);
      });
      host.hidden = false;
      host.removeAttribute('hidden');
    }

    function setCardVisible(card, visible) {
      if (visible) {
        card.classList.remove('parts-finder-is-hidden');
        card.hidden = false;
        card.removeAttribute('hidden');
        card.style.removeProperty('display');
        card.removeAttribute('aria-hidden');
      } else {
        card.classList.add('parts-finder-is-hidden');
        card.hidden = true;
        card.setAttribute('hidden', 'hidden');
        card.setAttribute('aria-hidden', 'true');
      }
    }

    function applyFilter(machine) {
      selectedMachine = machine;
      var emptyEl = document.querySelector('[data-parts-empty-filter]');
      var gridEl = getGrid();
      var cards = getCards();

      if (!machine) {
        setCollectionHidden(false);
        renderResults([]);
        for (var i = 0; i < cards.length; i++) setCardVisible(cards[i], true);
        if (emptyEl) emptyEl.hidden = true;
        if (gridEl) gridEl.setAttribute('data-filtered', 'false');
        showChip(false);
        showClearBtn(false);
        setStatus('', null);
        syncUrl('');
        reinitLazyImages();
        return 0;
      }

      if (!catalogReady) {
        pendingMachine = machine;
        showChip(true);
        showClearBtn(true);
        setStatus('Loading spare parts…', null);
        syncUrl(machine.sku);
        return 0;
      }

      var matches = [];
      var handles = Object.keys(partsCatalog);
      for (var c = 0; c < handles.length; c++) {
        var entry = partsCatalog[handles[c]];
        var desc = (entry && entry.description) || '';
        if (!String(desc).trim()) continue;
        var compat = C.extractCompat(desc, []);
        var result = C.partMatchesMachine(compat, machine, {
          includeUniversal: includeUniversal,
        });
        if (result.match) matches.push(entry);
      }

      matches.sort(function (a, b) {
        return String(a.title || '').localeCompare(String(b.title || ''));
      });

      renderResults(matches);
      setCollectionHidden(true);
      if (gridEl) gridEl.setAttribute('data-filtered', 'true');
      if (emptyEl) emptyEl.hidden = matches.length !== 0;

      showChip(true);
      showClearBtn(true);
      if (chipText) {
        chipText.textContent =
          chipPrefix +
          ' ' +
          (machine.title || '') +
          (machine.sku ? ' · Product Number ' + machine.sku : '');
      }
      var visible = matches.length;
      var msg = resultsTemplate
        ? resultsTemplate
            .replace(/\[count\]/gi, String(visible))
            .replace(/\[product_number\]/gi, machine.sku || '')
        : visible + ' spare parts for ' + (machine.sku || '');
      setStatus(msg, visible ? 'success' : 'error');
      syncUrl(machine.sku);
      return visible;
    }

    function syncUrl(pn) {
      try {
        var url = new URL(window.location.href);
        if (pn) url.searchParams.set('pn', pn);
        else url.searchParams.delete('pn');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      } catch (e) {}
    }

    function preservePnOnFilterLinks() {
      var pn = '';
      try {
        pn = new URLSearchParams(window.location.search).get('pn') || '';
      } catch (e) {}
      if (!pn && selectedMachine && selectedMachine.sku) pn = selectedMachine.sku;
      if (!pn) return;

      var anchors = document.querySelectorAll(
        'a[href*="filter."], a[href*="sort_by="], a[href*="page="], .js-remove-filter, .js-pagination a, .product-filters a'
      );
      for (var i = 0; i < anchors.length; i++) {
        var a = anchors[i];
        try {
          var href = a.getAttribute('href');
          if (!href || href.charAt(0) === '#') continue;
          var u = new URL(href, window.location.origin);
          if (!u.searchParams.get('pn')) {
            u.searchParams.set('pn', pn);
            a.setAttribute('href', u.pathname + u.search + u.hash);
          }
        } catch (e) {}
      }
    }

    function selectMachine(machine) {
      if (!machine) return;
      input.value = machine.sku || machine.title || '';
      hideList();
      applyFilter(machine);
    }

    function submitQuery(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      var q = (input.value || '').trim();
      if (!q) {
        clearFilter();
        return false;
      }

      if (!machines.length) {
        setStatus(emptyMachinesText, 'error');
        showClearBtn(true);
        return false;
      }

      var machine = C.findMachineByProductNumber(machines, q);
      var qCompact = C.compactSku(q);
      if (machine && qCompact && qCompact.length < 4) {
        var prefixHits = [];
        for (var p = 0; p < machines.length; p++) {
          if (C.tokenMatchesMachine(qCompact, machines[p])) prefixHits.push(machines[p]);
        }
        if (prefixHits.length > 1) {
          renderSuggestions(prefixHits);
          setStatus('Select a heat press from the list.', null);
          return false;
        }
      }

      if (!machine) {
        var listed = C.filterMachinesForAutocomplete(machines, q, 8);
        if (listed.length) renderSuggestions(listed);
        selectedMachine = null;
        showChip(false);
        showClearBtn(true);
        setCollectionHidden(false);
        renderResults([]);
        var cards = getCards();
        for (var i = 0; i < cards.length; i++) setCardVisible(cards[i], true);
        var emptyEl = document.querySelector('[data-parts-empty-filter]');
        if (emptyEl) emptyEl.hidden = true;
        setStatus(unknownText, 'error');
        syncUrl(q);
        reinitLazyImages();
        return false;
      }

      selectMachine(machine);
      return false;
    }

    function clearFilter(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      input.value = '';
      selectedMachine = null;
      hideList();
      showChip(false);
      showClearBtn(false);
      applyFilter(null);
      try {
        input.focus({ preventScroll: true });
      } catch (err) {
        input.focus();
      }
      return false;
    }

    submitBtn.addEventListener('click', submitQuery);

    // Event delegation so chip clear stays clickable even if nodes re-render
    root.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var clearEl = target.closest('[data-parts-chip-clear], [data-parts-clear]');
      if (!clearEl || !root.contains(clearEl)) return;
      e.preventDefault();
      e.stopPropagation();
      clearFilter(e);
    });

    var form = input.closest('form');
    if (form) form.addEventListener('submit', submitQuery);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (listbox && !listbox.hidden && activeIndex >= 0 && suggestions[activeIndex]) {
          selectMachine(suggestions[activeIndex]);
        } else {
          submitQuery(e);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (listbox && listbox.hidden) {
          renderSuggestions(C.filterMachinesForAutocomplete(machines, input.value, 8));
        }
        highlight(Math.min(suggestions.length - 1, activeIndex + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlight(Math.max(0, activeIndex - 1));
      } else if (e.key === 'Escape') {
        hideList();
      }
    });

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (!q) {
        hideList();
        return;
      }
      renderSuggestions(C.filterMachinesForAutocomplete(machines, q, 8));
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) hideList();
      setTimeout(preservePnOnFilterLinks, 0);
    });

    preservePnOnFilterLinks();

    // Only watch direct product-grid children — avoid deep subtree thrashing lazy images
    var grid = getGrid();
    if (grid && typeof MutationObserver !== 'undefined') {
      var obs = new MutationObserver(function (mutations) {
        var added = false;
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            added = true;
            break;
          }
        }
        if (!added) return;
        if (filterTimer) clearTimeout(filterTimer);
        filterTimer = setTimeout(function () {
          if (selectedMachine) applyFilter(selectedMachine);
          else reinitLazyImages();
          preservePnOnFilterLinks();
        }, 150);
      });
      obs.observe(grid, { childList: true, subtree: false });
    }

    // After theme filters re-render the section, re-apply machine filter + lazy images
    document.addEventListener('theme:customizer:loaded', function () {
      if (selectedMachine) applyFilter(selectedMachine);
      else reinitLazyImages();
      preservePnOnFilterLinks();
    });

    console.info(
      '[PartsFinder] ready — machines:',
      machines.length,
      'catalog keys:',
      Object.keys(partsCatalog).length,
      'cards:',
      getCards().length
    );

    if (!machines.length) {
      setStatus(emptyMachinesText, 'error');
    }

    loadFullCatalog();

    try {
      var params = new URLSearchParams(window.location.search);
      var pn = params.get('pn');
      if (pn) {
        input.value = pn;
        submitQuery();
      } else {
        reinitLazyImages();
      }
    } catch (e) {
      reinitLazyImages();
    }
  }

  whenReady(function () {
    waitForCompat(init);
  });
})();
